import { defineColumns } from './piColumns.js';
import { EMPTY, formatDecimal, formatInteger, formatPercent, isNumber } from './piFormat.js';
import { indicatorCount, indicatorValue } from './piOverviewModel.js';
import {
  MODULE_OPTIONS, PLATFORM_OPTIONS, ROLE_CLASS_OPTIONS, SIZE_BAND_OPTIONS, journeyLabel, journeyText, optionLabel, platformNote, profileText,
} from './piQuery.js';
import { listOf, objectOf } from './piRequests.js';
import { ACTIVE_30D_DESCRIPTION, countText, metricCell, missingActiveTitle, numberOf, ratioText, shareOf } from './piTable.js';

export const SEGMENT_DIMENSIONS = [
  { value: 'roleClass', label: 'Papel do usuário' },
  { value: 'module', label: 'Módulo' },
  { value: 'platform', label: 'Plataforma' },
  { value: 'sizeBand', label: 'Porte (funcionários)' },
  { value: 'segment', label: 'Perfil da empresa' },
];

export const DEFAULT_DIMENSION = 'roleClass';

export const DISTRIBUTION_BANDS = [
  { band: '0', label: 'Sem ativos' },
  { band: '1-5', label: '1 a 5 ativos' },
  { band: '6-20', label: '6 a 20 ativos' },
  { band: '21-50', label: '21 a 50 ativos' },
  { band: '51-200', label: '51 a 200 ativos' },
  { band: '200+', label: 'Mais de 200 ativos' },
];

export const ACTIVE_SERIES = [
  { key: 'dau', field: 'dau', label: 'DAU (ativos no dia)', window: 1 },
  { key: 'wau', field: 'wau', label: 'WAU (ativos em 7 dias)', window: 7 },
  { key: 'mau', field: 'mau', label: 'MAU (ativos em 30 dias)', window: 30 },
];

export const IN_USE_SERIES = [
  { key: 'inUseD', field: 'inUseD', label: 'Em uso no dia', window: 1 },
  { key: 'inUseW', field: 'inUseW', label: 'Em uso em 7 dias', window: 7 },
  { key: 'inUseM', field: 'inUseM', label: 'Em uso em 30 dias', window: 30 },
];

export const COMPANY_SERIES = [
  { key: 'activeCompaniesD', field: 'activeCompaniesD', label: 'Empresas ativas no dia', window: 1 },
  { key: 'activeCompaniesW', field: 'activeCompaniesW', label: 'Empresas ativas em 7 dias', window: 7 },
  { key: 'activeCompaniesM', field: 'activeCompaniesM', label: 'Empresas ativas em 30 dias', window: 30 },
];

export const DEFAULT_COMPANY_SORT = { sort: 'activeUsers', desc: true };

const DIMENSION_OPTIONS = {
  roleClass: ROLE_CLASS_OPTIONS,
  module: MODULE_OPTIONS,
  platform: PLATFORM_OPTIONS,
  sizeBand: SIZE_BAND_OPTIONS,
};

const textOf = (value) => (value === null || value === undefined ? '' : String(value));

const cellMetric = (field) => (row) => {
  const cell = objectOf(row)[field];
  return cell && cell.metric ? cell.metric : null;
};

export const validDimension = (value) => {
  for (let i = 0; i < SEGMENT_DIMENSIONS.length; i++) {
    if (SEGMENT_DIMENSIONS[i].value === value) return value;
  }
  return DEFAULT_DIMENSION;
};

export const dimensionLabel = (dimension, key, label) => {
  if (label) return String(label);
  const options = DIMENSION_OPTIONS[dimension];
  const raw = textOf(key);
  if (!raw) return 'Sem valor';
  return options ? optionLabel(options, raw) : raw;
};

export const sortedPoints = (points) => listOf(points)
  .filter((point) => typeof point.date === 'string' && point.date)
  .slice()
  .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

export const ratioPct = (numerator, denominator) => shareOf(numerator, denominator);

export const stickinessOf = (point) => {
  const source = point || {};
  const computed = ratioPct(source.dau, source.mau);
  if (computed !== null) return computed;
  return isNumber(source.stickiness) ? source.stickiness * 100 : null;
};

const seriesFrom = (points, specs) => specs.map((spec) => ({
  key: spec.key,
  label: spec.label,
  area: !!spec.area,
  window: spec.window || 1,
  points: points.map((point) => ({
    date: point.date,
    value: isNumber(point[spec.field]) ? point[spec.field] : null,
    partial: point.isPartial === true,
  })),
}));

export const engagementCharts = (points) => {
  const sorted = sortedPoints(points);
  return {
    active: seriesFrom(sorted, ACTIVE_SERIES),
    inUse: seriesFrom(sorted, IN_USE_SERIES),
    companies: seriesFrom(sorted, COMPANY_SERIES),
    stickiness: [
      {
        key: 'dauMau',
        label: 'DAU/MAU',
        area: true,
        window: 30,
        points: sorted.map((point) => ({ date: point.date, value: stickinessOf(point), partial: point.isPartial === true })),
      },
      {
        key: 'wauMau',
        label: 'WAU/MAU',
        area: false,
        window: 30,
        points: sorted.map((point) => ({ date: point.date, value: ratioPct(point.wau, point.mau), partial: point.isPartial === true })),
      },
    ],
  };
};

const hoverItem = (label, text) => (text ? { label: label, text: text } : null);

const pctText = (numerator, denominator) => {
  const value = ratioPct(numerator, denominator);
  return value === null ? null : formatPercent(value);
};

const openedWithoutActing = (point) => (isNumber(point.inUseD) && isNumber(point.dau) ? formatInteger(Math.max(0, point.inUseD - point.dau)) : null);

const pairText = (numerator, denominator) => (isNumber(numerator) && isNumber(denominator) ? formatInteger(numerator) + ' ÷ ' + formatInteger(denominator) : null);

export const engagementTooltipExtras = (points, granularity) => {
  const daily = !granularity || granularity === 'day';
  const byDate = {};
  sortedPoints(points).forEach((point) => {
    byDate[point.date] = point;
  });
  const at = (date) => byDate[date] || {};
  return {
    active: (date) => {
      const point = at(date);
      return [hoverItem('DAU/MAU (hábito)', pctText(point.dau, point.mau)), daily ? hoverItem('Abriram sem agir no dia', openedWithoutActing(point)) : null];
    },
    inUse: (date) => {
      const point = at(date);
      if (!daily) {
        const monthGap = isNumber(point.inUseM) && isNumber(point.mau) ? formatInteger(Math.max(0, point.inUseM - point.mau)) : null;
        return [hoverItem('Abriram sem agir em 30 dias', monthGap)];
      }
      return [hoverItem('Dos que abriram, agiram', pctText(point.dau, point.inUseD)), hoverItem('Abriram sem agir no dia', openedWithoutActing(point))];
    },
    stickiness: (date) => {
      const point = at(date);
      return [hoverItem('DAU ÷ MAU', pairText(point.dau, point.mau)), hoverItem('WAU ÷ MAU', pairText(point.wau, point.mau))];
    },
    companies: (date) => {
      const point = at(date);
      const perCompany = isNumber(point.mau) && isNumber(point.activeCompaniesM) && point.activeCompaniesM > 0
        ? formatDecimal(point.mau / point.activeCompaniesM, 1) : null;
      return [hoverItem('Ativos por empresa (30 dias)', perCompany)];
    },
  };
};

export const latestPoint = (points) => {
  const sorted = sortedPoints(points);
  if (!sorted.length) return null;
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (sorted[i].isPartial !== true) return sorted[i];
  }
  return sorted[sorted.length - 1];
};

export const HEADLINE_INFO = {
  dau: 'engagement.dau',
  wau: 'engagement.wau',
  mau: 'engagement.mau',
  stickiness: 'engagement.stickiness',
  wauMau: 'engagement.wauMau',
  inUseM: 'engagement.inUse',
  activeCompaniesM: 'engagement.activeCompaniesMonth',
};

export const ENGAGEMENT_SERIES_COLUMNS = defineColumns([
  { key: 'date', label: 'Data', type: 'date', infoKey: 'engagement.pointDate' },
  { key: 'dau', label: 'DAU (ativos no dia)', type: 'decimal', infoKey: 'engagement.dau' },
  { key: 'wau', label: 'WAU (ativos em 7 dias)', type: 'int', infoKey: 'engagement.wau' },
  { key: 'mau', label: 'MAU (ativos em 30 dias)', type: 'int', infoKey: 'engagement.mau' },
  { key: 'dauMau', label: 'DAU/MAU', type: 'percent', infoKey: 'engagement.stickiness', value: (point) => stickinessOf(point) },
  { key: 'wauMau', label: 'WAU/MAU', type: 'percent', infoKey: 'engagement.wauMau', value: (point) => ratioPct(objectOf(point).wau, objectOf(point).mau) },
  { key: 'inUseD', label: 'Em uso no dia', type: 'int', infoKey: 'engagement.inUseDay' },
  { key: 'inUseW', label: 'Em uso em 7 dias', type: 'int', infoKey: 'engagement.inUseWeek' },
  { key: 'inUseM', label: 'Em uso em 30 dias', type: 'int', infoKey: 'engagement.inUse' },
  { key: 'activeCompaniesD', label: 'Empresas ativas no dia', type: 'int', infoKey: 'engagement.activeCompaniesDay' },
  { key: 'activeCompaniesW', label: 'Empresas ativas em 7 dias', type: 'int', infoKey: 'engagement.activeCompaniesWeek' },
  { key: 'activeCompaniesM', label: 'Empresas ativas em 30 dias', type: 'int', infoKey: 'engagement.activeCompaniesMonth' },
  { key: 'isPartial', label: 'Parcial', type: 'text', infoKey: 'common.state.partial', value: (point) => objectOf(point).isPartial === true },
]);

export const engagementHeadline = (points) => {
  const point = latestPoint(points);
  if (!point) return [];
  const stickiness = stickinessOf(point);
  const wauMau = ratioPct(point.wau, point.mau);
  return [
    { key: 'dau', label: 'DAU', text: countText(point.dau), sub: '' },
    { key: 'wau', label: 'WAU', text: countText(point.wau), sub: '' },
    { key: 'mau', label: 'MAU', text: countText(point.mau), sub: '' },
    { key: 'stickiness', label: 'DAU/MAU', text: stickiness === null ? 'n/d' : formatPercent(stickiness), sub: ratioText(point.dau, point.mau) },
    { key: 'wauMau', label: 'WAU/MAU', text: wauMau === null ? 'n/d' : formatPercent(wauMau), sub: ratioText(point.wau, point.mau) },
    { key: 'inUseM', label: 'Em uso (30 dias)', text: countText(point.inUseM), sub: '' },
    { key: 'activeCompaniesM', label: 'Empresas ativas (30 dias)', text: countText(point.activeCompaniesM), sub: '' },
  ].map((item) => Object.assign(item, { date: point.date, partial: point.isPartial === true, infoKey: HEADLINE_INFO[item.key] }));
};

export const distributionRows = (distribution) => {
  const rows = listOf(distribution);
  const byBand = {};
  rows.forEach((row) => {
    byBand[textOf(row.band)] = row;
  });
  const known = DISTRIBUTION_BANDS.map((band) => band.band);
  const ordered = DISTRIBUTION_BANDS.map((band) => ({ band: band.band, label: band.label, companies: byBand[band.band] ? byBand[band.band].companies : null }));
  rows.forEach((row) => {
    if (known.indexOf(textOf(row.band)) === -1) ordered.push({ band: textOf(row.band), label: textOf(row.band) || 'Sem faixa', companies: row.companies });
  });
  const total = ordered.reduce((sum, row) => sum + (isNumber(row.companies) ? row.companies : 0), 0);
  return ordered.map((row) => {
    const share = shareOf(row.companies, total);
    return {
      key: row.band,
      label: row.label,
      value: isNumber(row.companies) ? row.companies : null,
      share: share,
      valueText: isNumber(row.companies)
        ? formatInteger(row.companies) + (row.companies === 1 ? ' empresa' : ' empresas') + (share === null ? '' : ' (' + formatPercent(share) + ')')
        : EMPTY,
      title: row.label + ': ' + (isNumber(row.companies) ? formatInteger(row.companies) : EMPTY),
    };
  });
};

export const ENGAGEMENT_DISTRIBUTION_COLUMNS = defineColumns([
  { key: 'label', label: 'Faixa de usuários ativos (30 dias)', type: 'text', infoKey: 'engagement.distributionBand' },
  { key: 'value', label: 'Empresas', type: 'int', infoKey: 'engagement.distributionCompanies' },
  { key: 'share', label: '% das empresas', type: 'percent', infoKey: 'engagement.distributionShare' },
]);

export const distributionTotal = (distribution) =>
  listOf(distribution).reduce((sum, row) => sum + (isNumber(row.companies) ? row.companies : 0), 0);

export const segmentNote = (dimension, key) => (dimension === 'platform' ? platformNote(key) : '');

export const segmentRows = (rows, dimension, compare) => listOf(rows).map((row, index) => ({
  key: textOf(row.key) || String(index),
  label: dimensionLabel(dimension, row.key, row.label),
  note: segmentNote(dimension, row.key),
  activeUsers: metricCell(row.activeUsers, { compare: compare }),
  inUseUsers: metricCell(row.inUseUsers, { compare: compare }),
  openedWithoutAction: metricCell(row.openedWithoutAction, { compare: compare }),
  share: isNumber(row.share) ? row.share : null,
  shareText: isNumber(row.share) ? formatPercent(row.share) : EMPTY,
}));

export const SEAT_CARDS = [
  { key: 'activeSeatsRate', label: 'Active Seats Rate', definitionKey: 'active_seats_rate', infoKey: 'common.activeSeatsRate' },
  { key: 'contractUtilization', label: 'Utilização do contrato', definitionKey: 'contract_utilization', infoKey: 'common.contractUtilization' },
  { key: 'was', label: 'WAS (assentos em uso na semana)', definitionKey: 'was', infoKey: 'engagement.was' },
];

export const SEAT_COUNTS = [
  { key: 'active', label: 'Ativos habilitados (30 dias)', infoKey: 'engagement.activeEnabled' },
  { key: 'enabled', label: 'Habilitados elegíveis', infoKey: 'engagement.enabledEligible' },
  { key: 'contracted', label: 'Assentos contratados', infoKey: 'engagement.seatsContracted' },
];

export const ENGAGEMENT_SEATS_COLUMNS = defineColumns([
  { key: 'period', label: 'Período', type: 'text', infoKey: 'filter.period', screen: false },
  { key: 'activeSeatsRate', label: 'Active Seats Rate', type: 'metric', unit: 'pct', infoKey: 'common.activeSeatsRate', value: indicatorValue('activeSeatsRate') },
  {
    key: 'contractUtilization', label: 'Utilização do contrato', type: 'metric', unit: 'pct', infoKey: 'common.contractUtilization',
    value: indicatorValue('contractUtilization'),
  },
  { key: 'was', label: 'WAS (assentos em uso na semana)', type: 'metric', unit: 'pct', infoKey: 'engagement.was', value: indicatorValue('was') },
  { key: 'active', label: 'Ativos habilitados (30 dias)', type: 'int', infoKey: 'engagement.activeEnabled', value: indicatorCount('active') },
  { key: 'enabled', label: 'Habilitados elegíveis', type: 'int', infoKey: 'engagement.enabledEligible', value: indicatorCount('enabled') },
  { key: 'contracted', label: 'Assentos contratados', type: 'int', infoKey: 'engagement.seatsContracted', value: indicatorCount('contracted') },
]);

export const ENGAGEMENT_SEAT_CUT_COLUMNS = defineColumns([
  { key: 'cut', label: 'Corte', type: 'text', infoKey: 'engagement.seatsCut', screen: false },
  { key: 'label', label: 'Recorte', type: 'text', infoKey: 'engagement.seatsCutValue' },
  { key: 'activeSeatsRate', label: 'Active Seats Rate', type: 'metric', unit: 'pct', infoKey: 'engagement.seatsCutRate', value: cellMetric('cell') },
]);

export const SEAT_BREAKDOWNS = [
  { key: 'byRoleClass', dimension: 'roleClass', label: 'Por perfil' },
  { key: 'byModule', dimension: 'module', label: 'Por módulo' },
  { key: 'bySizeBand', dimension: 'sizeBand', label: 'Por porte' },
];

export const seatsView = (seats, compare) => {
  const data = objectOf(seats);
  const overall = objectOf(data.overall);
  return {
    cards: SEAT_CARDS.map((card) => ({
      key: card.key,
      label: card.label,
      definitionKey: card.definitionKey,
      infoKey: card.infoKey,
      metric: overall[card.key] && typeof overall[card.key] === 'object' ? overall[card.key] : null,
    })),
    counts: SEAT_COUNTS.map((count) => ({
      key: count.key,
      label: count.label,
      infoKey: count.infoKey,
      value: isNumber(overall[count.key]) ? overall[count.key] : null,
      text: countText(overall[count.key]),
    })),
    breakdowns: SEAT_BREAKDOWNS.map((breakdown) => ({
      key: breakdown.key,
      label: breakdown.label,
      rows: listOf(data[breakdown.key]).map((row, index) => ({
        key: textOf(row.key) || String(index),
        label: dimensionLabel(breakdown.dimension, row.key, row.label),
        cell: metricCell(row.activeSeatsRate, { compare: compare }),
      })),
    })),
    hasData: Object.keys(overall).length > 0 || SEAT_BREAKDOWNS.some((breakdown) => listOf(data[breakdown.key]).length > 0),
  };
};

export const seatCutRows = (breakdowns) => listOf(breakdowns).reduce((all, breakdown) => all.concat(listOf(breakdown.rows).map((row) => ({
  key: textOf(breakdown.key) + ':' + textOf(row.key),
  cut: textOf(breakdown.label),
  label: textOf(row.label),
  cell: row.cell,
}))), []);

export const seatCounts = (counts) => {
  const values = {};
  listOf(counts).forEach((count) => {
    values[count.key] = count.value;
  });
  return values;
};

const dimensionColumn = (dimension, label, infoKey) => ({ key: dimension, label: label, type: 'text', infoKey: infoKey, value: (row) => objectOf(row).label });

export const ENGAGEMENT_SEGMENT_COLUMNS = defineColumns([
  dimensionColumn('roleClass', 'Papel do usuário', 'engagement.segmentRole'),
  dimensionColumn('module', 'Módulo', 'engagement.segmentModule'),
  dimensionColumn('platform', 'Plataforma', 'engagement.segmentPlatform'),
  dimensionColumn('sizeBand', 'Porte (funcionários)', 'common.sizeBand'),
  dimensionColumn('segment', 'Perfil da empresa', 'common.segment'),
  { key: 'activeUsers', label: 'Ativos', type: 'metric', unit: 'count', infoKey: 'engagement.segmentActive', value: cellMetric('activeUsers') },
  { key: 'inUseUsers', label: 'Em uso', type: 'metric', unit: 'count', infoKey: 'engagement.segmentInUse', value: cellMetric('inUseUsers') },
  {
    key: 'openedWithoutAction', label: 'Abriram sem agir', type: 'metric', unit: 'count', infoKey: 'engagement.openedWithoutAction',
    value: cellMetric('openedWithoutAction'),
  },
  { key: 'share', label: 'Participação nos ativos', type: 'percent', infoKey: 'engagement.segmentShare' },
  { key: 'note', label: 'Nota', type: 'text', infoKey: 'engagement.segmentNote', screen: false },
]);

export const segmentColumns = (dimension) => {
  const current = validDimension(dimension);
  const others = SEGMENT_DIMENSIONS.map((option) => option.value).filter((value) => value !== current);
  return ENGAGEMENT_SEGMENT_COLUMNS.filter((column) => others.indexOf(column.key) === -1);
};

export const ENGAGEMENT_COMPANY_COLUMNS = defineColumns([
  {
    key: 'name', label: 'Empresa', type: 'text', infoKey: 'common.company', sortable: true, startDescending: false, width: 40,
    value: (row) => textOf(objectOf(row).name) || textOf(objectOf(row).companyId),
  },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  {
    key: 'sizeBand', label: 'Porte', type: 'text', infoKey: 'common.sizeBand', screen: false,
    value: (row) => (objectOf(row).sizeBand ? optionLabel(SIZE_BAND_OPTIONS, objectOf(row).sizeBand) : ''),
  },
  { key: 'segment', label: 'Perfil da empresa', type: 'text', infoKey: 'common.segment', screen: false, value: (row) => textOf(objectOf(row).segment).trim() },
  { key: 'clientJourney', label: 'Jornada', type: 'text', infoKey: 'common.journey', screen: false, value: (row) => journeyLabel(objectOf(row).clientJourney) },
  { key: 'signal', label: 'Queda de atividade', type: 'text', infoKey: 'common.signalDrop', screen: false, value: (row) => objectOf(row).signal === 'drop' },
  {
    key: 'activeUsers', label: 'Ativos (30 dias)', type: 'metric', unit: 'count', infoKey: 'common.activeUsers30d', sortable: true, startDescending: true,
    description: ACTIVE_30D_DESCRIPTION,
  },
  { key: 'inUseUsers', label: 'Em uso (30 dias)', type: 'int', infoKey: 'engagement.companyInUse', screen: false, value: (row) => numberOf(objectOf(row).inUseUsers) },
  { key: 'enabledUsers', label: 'Habilitados', type: 'int', infoKey: 'common.enabledUsers', screen: false },
  { key: 'activeSeatsRate', label: 'Active Seats Rate', type: 'metric', unit: 'pct', infoKey: 'common.activeSeatsRate', sortable: true, startDescending: true },
  {
    key: 'contractUtilization', label: 'Utilização do contrato', type: 'metric', unit: 'pct', infoKey: 'common.contractUtilization', sortable: true,
    startDescending: true,
  },
  { key: 'seatsContracted', label: 'Assentos contratados', type: 'int', infoKey: 'common.seatsContracted', screen: false },
  { key: 'trend', label: 'Tendência', type: 'text', infoKey: 'common.trend', export: false },
]);

const activeCell = (row, compare) => {
  const cell = metricCell(row.activeUsers, { compare: compare, ratio: false });
  const missing = !cell.visible && cell.metric.state === 'na';
  return { cell: cell, missing: missing, title: missing ? missingActiveTitle(row) : '' };
};

export const engagementCompanyRows = (rows, compare) => listOf(rows).map((row, index) => {
  const active = activeCell(row, compare);
  return {
    key: textOf(row.shardCode) + ':' + (textOf(row.companyId) || String(index)),
    companyId: textOf(row.companyId),
    shardCode: textOf(row.shardCode),
    name: textOf(row.name) || textOf(row.companyId) || 'Empresa',
    sizeBandText: row.sizeBand ? optionLabel(SIZE_BAND_OPTIONS, row.sizeBand) : EMPTY,
    segmentText: profileText(textOf(row.segment)) || EMPTY,
    activeUsers: active.cell,
    activeMissing: active.missing,
    activeTitle: active.title,
    inUseText: countText(numberOf(row.inUseUsers)),
    enabledText: countText(row.enabledUsers),
    seatsText: countText(row.seatsContracted),
    activeSeatsRate: metricCell(row.activeSeatsRate, { compare: compare }),
    contractUtilization: metricCell(row.contractUtilization, { compare: compare }),
    trend: listOf(row.trend),
    drop: row.signal === 'drop',
    journeyText: journeyText(row.clientJourney),
  };
});



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piEngagementModel.js